import React, { useState, useEffect } from "react";
import styles from "./student-list.module.css";
import { Popconfirm, Typography } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import CustomFilter from "../../../../components/custom-filter/custom-filter";
import { useLocation, useNavigate } from "react-router-dom";
import CustomTextfield from "../../../../common/input/custom-textfield/custom-textfield";
import SVGIcon from "../../../../constants/SVGIcon";
import CustomTable from "../../../../common/custom-table/custom-table";
import RoutesConstant from "../../../../constants/routes";
import { deleteStudent, exportStudents, fetchStudentList } from "../../../../services/student";
import CustomProfileAvatar from "../../../../common/custom-profile-avatar/custom-profile-avatar";
import { formatDate } from "../../../../utils/format-date/format-date";
import { toast } from "react-toastify";
import { downloadFile } from "../../../../utils/common";
import CustomButton from "../../../../common/custom-button/custom-button";

const StudentList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [studentList, setStudentList] = useState([]);
  const [filterValues, setFilterValues] = useState({
    status: [],
  });
  const searchParams = new URLSearchParams(location.search);
  const levelNameParam = searchParams.get("level") || "";
  const classNameParam = searchParams.get("class") || "";
  const classroomID = searchParams.get("id") || "";
  const yearParam = searchParams.get("year-id") || "";
  const yearNameParam = searchParams.get("year-name") || "";

  const filterData = [
    {
      id: "status",
      label: "Status",
      data: [],
    },
  ];

  const columns = [
    {
      title: "Reg No.",
      dataIndex: "user_name",
      key: "user_name",
      render: (value) => value ?? "-",
    },
    {
      title: "Student Name",
      dataIndex: "name",
      key: "name",
      render: (value, record) => (
        <div className={styles.nameContainer}>
          <CustomProfileAvatar
            image={
              record?.profile_picture
                ? record?.profile_picture
                : "sample-user.png"
            }
            imageName={record?.name}
            imageSrc={
              record?.profile_picture ?? "assets/images/sample-user.png"
            }
            divBackgroundColor={"#97a997"}
          />
          <Typography>{value}</Typography>
        </div>
      ),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (value) => value ?? "-",
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      render: (_, record) => record?.classroom?.level?.name ?? "-",
    },
    {
      title: "Class",
      dataIndex: "class",
      key: "class",
      render: (_, record) => record?.classroom?.name ?? "-",
    },
    {
      title: "Date of birth",
      dataIndex: "date_of_birth",
      key: "date_of_birth",
      render: (value) => (value ? formatDate(value) : "-"),
    },
    {
      title: "Parent Phone No.",
      dataIndex: "parent_phone_number",
      key: "parent_phone_number",
      render: (_, record) =>
        record?.parents?.length ? record?.parents[0]?.phone_number : "-",
    },
  ];

  const handleDeleteStudent = async (studentId) => {
    try {
      setLoading(true);
      await deleteStudent(studentId);
      await getStudentList();
      toast.success("Student is deleted");
    } catch (err) {
      toast.error("Error Deleting Student");
    } finally {
      setLoading(false);
    }
  };

  const handleExportStudents = async () => {
    try {
      setLoading(true);
      const payload = {
        classroom_id: classroomID,
        academic_year_id: yearParam,
      };
      const response = await exportStudents(payload);
      const contentDisposition = response.headers["content-disposition"];
      downloadFile(response.data, contentDisposition, "students.csv");
      toast.success("Students exported Successfully");
    } catch (error) {
      console.error("Failed to export students:", error);
      toast.error(error?.data?.message || "Failed to export students");
    } finally {
      setLoading(false);
    }
  };

  const menuItems = (record) => [
    {
      key: "1",
      label: "View Details",
      onClick: () =>
        navigate(`${RoutesConstant.STUDENT_DETAILS}?id=${record?.id}`),
    },
    {
      key: "2",
      label: "Edit",
      onClick: () =>
        navigate(`${RoutesConstant.STUDENT_FORM}?uid=${record?.id}`),
    },
    {
      key: "3",
      label: (
        <Popconfirm
          title={`Are you sure you want to delete ${record?.name}?`}
          onConfirm={() => handleDeleteStudent(record?.id)}
          okText="Yes"
          cancelText="No"
        >
          <span>Delete</span>
        </Popconfirm>
      ),
    },
  ];

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setPageNo(1);
      getStudentList();
    }, 500);
    return () => {
      clearTimeout(timeOut);
    };
  }, [searchTerm, filterValues]);

  const getStudentList = async (tempPage = pageNo, tempPageSize = pageSize) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
        classroom_id: classroomID,
        academic_year_id: yearParam,
      };

      if (searchTerm) {
        payload.search = searchTerm;
      }
      const response = await fetchStudentList(payload);
      if (response?.data) {
        setStudentList(response?.data?.data?.students);
        setTotalCount(response?.data?.data?.totalRecords);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  const onChangeFilter = (id, values) => {
    const tempData = { ...filterValues, [id]: values };
    setFilterValues(tempData);
  };

  const onFilterReset = () => {
    setFilterValues({
      status: [],
    });
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getStudentList(page, pageSize);
  };

  return (
    <div className={styles.main_container}>
      <div className={styles.leftSection} onClick={() => navigate(-1)}>
        <LeftOutlined />
        <Typography className={styles.navTitle}>
          {`${levelNameParam}, ${classNameParam}`}
        </Typography>
      </div>
      <div className={styles.title_container}>
        <Typography className={styles.title}>
          <p className={styles.titleStudent}>Students</p>
          <span className={styles.yearText}>
            Academic Year: {yearNameParam}
          </span>
        </Typography>
        <div className={styles.view_style_options}>
          <div className={styles.filter_container}>
            {studentList?.length ? (
              <CustomButton
                title="Export Students"
                onClick={handleExportStudents}
              />
            ) : null}
            <CustomTextfield
              id={"search"}
              type={"text"}
              className={styles.inputField}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              suffix={
                <SVGIcon
                  name={"search-icon"}
                  width={"24"}
                  height={"24"}
                  className="anticon"
                  stroke={"#667085"}
                />
              }
              placeholder={"Search ..."}
            />
            <CustomFilter
              width={styles.filter_style}
              filterData={filterData}
              values={filterValues}
              onResetFilter={onFilterReset}
              onChange={(id, values) => onChangeFilter(id, values)}
            />
          </div>
        </div>
      </div>
      <CustomTable
        columns={columns}
        dataSource={studentList}
        total={totalCount}
        loading={loading}
        showMoreIcon={true}
        menuItems={menuItems}
        pageSize={pageSize}
        currentPage={pageNo}
        handlePageChange={handleChangePage}
      />
    </div>
  );
};

export default StudentList;
